// Unified enum definitions matching backend enums for consistent status handling

// ============================================
// MATCHDAY PLAN ENUMS
// ============================================

export enum MatchdayPlanStatus {
  Draft = 0,          // Entwurf - Initial planning phase
  InPlanning = 1,     // In Planung - Active planning, vehicles being assigned
  Confirmed = 2,      // Bestätigt - Plan finalized, ready for execution
  InProgress = 3,     // Unterwegs - Currently traveling/executing plan
  Completed = 4,      // Abgeschlossen - Successfully completed
  Cancelled = 5       // Abgesagt - Plan cancelled
}

export interface MatchdayPlanStatusInfo {
  value: MatchdayPlanStatus;
  label: string;
  color: string;
  icon: string;
}

export const MATCHDAY_PLAN_STATUS_CONFIGS: Record<MatchdayPlanStatus, MatchdayPlanStatusInfo> = {
  [MatchdayPlanStatus.Draft]: { value: MatchdayPlanStatus.Draft, label: 'Entwurf', color: '#6c757d', icon: '' },
  [MatchdayPlanStatus.InPlanning]: { value: MatchdayPlanStatus.InPlanning, label: 'In Planung', color: '#007bff', icon: '' },
  [MatchdayPlanStatus.Confirmed]: { value: MatchdayPlanStatus.Confirmed, label: 'Bestätigt', color: '#28a745', icon: '' },
  [MatchdayPlanStatus.InProgress]: { value: MatchdayPlanStatus.InProgress, label: 'Unterwegs', color: '#ffc107', icon: '' },
  [MatchdayPlanStatus.Completed]: { value: MatchdayPlanStatus.Completed, label: 'Abgeschlossen', color: '#28a745', icon: '' },
  [MatchdayPlanStatus.Cancelled]: { value: MatchdayPlanStatus.Cancelled, label: 'Abgesagt', color: '#dc3545', icon: '' }
};

// ============================================
// TRANSPORTATION ASSIGNMENT ENUMS
// ============================================

export enum TransportationAssignmentStatus {
  Planned = 0,        // Geplant - Initial assignment
  Confirmed = 1,      // Bestätigt - Assignment confirmed
  InTransit = 2,      // Unterwegs - Currently traveling
  Arrived = 3,        // Angekommen - Arrived at destination
  Completed = 4,      // Abgeschlossen - Trip completed (including return)
  Cancelled = 5,      // Storniert - Assignment cancelled
  Delayed = 6         // Verspätet - Delayed departure/arrival
}

export interface TransportationAssignmentStatusInfo {
  value: TransportationAssignmentStatus;
  label: string;
  color: string;
  icon: string;
}

export const TRANSPORTATION_ASSIGNMENT_STATUS_CONFIGS: Record<TransportationAssignmentStatus, TransportationAssignmentStatusInfo> = {
  [TransportationAssignmentStatus.Planned]: { value: TransportationAssignmentStatus.Planned, label: 'Geplant', color: '#6c757d', icon: '' },
  [TransportationAssignmentStatus.Confirmed]: { value: TransportationAssignmentStatus.Confirmed, label: 'Bestätigt', color: '#007bff', icon: '' },
  [TransportationAssignmentStatus.InTransit]: { value: TransportationAssignmentStatus.InTransit, label: 'Unterwegs', color: '#ffc107', icon: '' },
  [TransportationAssignmentStatus.Arrived]: { value: TransportationAssignmentStatus.Arrived, label: 'Angekommen', color: '#28a745', icon: '' },
  [TransportationAssignmentStatus.Completed]: { value: TransportationAssignmentStatus.Completed, label: 'Abgeschlossen', color: '#28a745', icon: '' },
  [TransportationAssignmentStatus.Cancelled]: { value: TransportationAssignmentStatus.Cancelled, label: 'Storniert', color: '#dc3545', icon: '' },
  [TransportationAssignmentStatus.Delayed]: { value: TransportationAssignmentStatus.Delayed, label: 'Verspätet', color: '#dc3545', icon: '' }
};

// ============================================
// DETAIL PLAN ENUMS
// ============================================

export enum DetailPlanStatus {
  Draft = 0,
  InPlanning = 1,
  Confirmed = 2,
  InProgress = 3,
  Completed = 4,
  Cancelled = 5,
  Delayed = 6,
  OnHold = 7
}

export interface DetailPlanStatusInfo {
  value: DetailPlanStatus;
  label: string;
  color: string;
  icon: string;
}

export const DETAIL_PLAN_STATUS_CONFIGS: Record<DetailPlanStatus, DetailPlanStatusInfo> = {
  [DetailPlanStatus.Draft]: { value: DetailPlanStatus.Draft, label: 'Entwurf', color: '#6c757d', icon: '' },
  [DetailPlanStatus.InPlanning]: { value: DetailPlanStatus.InPlanning, label: 'In Planung', color: '#007bff', icon: '' },
  [DetailPlanStatus.Confirmed]: { value: DetailPlanStatus.Confirmed, label: 'Bestätigt', color: '#28a745', icon: '' },
  [DetailPlanStatus.InProgress]: { value: DetailPlanStatus.InProgress, label: 'In Bearbeitung', color: '#ffc107', icon: '' },
  [DetailPlanStatus.Completed]: { value: DetailPlanStatus.Completed, label: 'Abgeschlossen', color: '#28a745', icon: '' },
  [DetailPlanStatus.Cancelled]: { value: DetailPlanStatus.Cancelled, label: 'Abgebrochen', color: '#dc3545', icon: '' },
  [DetailPlanStatus.Delayed]: { value: DetailPlanStatus.Delayed, label: 'Verspätet', color: '#dc3545', icon: '' },
  [DetailPlanStatus.OnHold]: { value: DetailPlanStatus.OnHold, label: 'Pausiert', color: '#fd7e14', icon: '' }
};

// ============================================
// VEHICLE ASSIGNMENT ENUMS
// ============================================

export enum VehicleAssignmentStatus {
  Planned = 0,
  Confirmed = 1,
  DriverAssigned = 2,
  PreTripCheck = 3,
  Ready = 4,
  InTransit = 5,
  Arrived = 6,
  Completed = 7,
  Cancelled = 8,
  Delayed = 9,
  Breakdown = 10,
  Maintenance = 11
}

export interface VehicleAssignmentStatusInfo {
  value: VehicleAssignmentStatus;
  label: string;
  color: string;
  icon: string;
}

export const VEHICLE_ASSIGNMENT_STATUS_CONFIGS: Record<VehicleAssignmentStatus, VehicleAssignmentStatusInfo> = {
  [VehicleAssignmentStatus.Planned]: { value: VehicleAssignmentStatus.Planned, label: 'Geplant', color: '#6c757d', icon: '' },
  [VehicleAssignmentStatus.Confirmed]: { value: VehicleAssignmentStatus.Confirmed, label: 'Bestätigt', color: '#007bff', icon: '' },
  [VehicleAssignmentStatus.DriverAssigned]: { value: VehicleAssignmentStatus.DriverAssigned, label: 'Fahrer zugewiesen', color: '#17a2b8', icon: '' },
  [VehicleAssignmentStatus.PreTripCheck]: { value: VehicleAssignmentStatus.PreTripCheck, label: 'Fahrzeugkontrolle', color: '#6f42c1', icon: '' },
  [VehicleAssignmentStatus.Ready]: { value: VehicleAssignmentStatus.Ready, label: 'Bereit', color: '#28a745', icon: '' },
  [VehicleAssignmentStatus.InTransit]: { value: VehicleAssignmentStatus.InTransit, label: 'Unterwegs', color: '#ffc107', icon: '' },
  [VehicleAssignmentStatus.Arrived]: { value: VehicleAssignmentStatus.Arrived, label: 'Angekommen', color: '#28a745', icon: '' },
  [VehicleAssignmentStatus.Completed]: { value: VehicleAssignmentStatus.Completed, label: 'Abgeschlossen', color: '#28a745', icon: '' },
  [VehicleAssignmentStatus.Cancelled]: { value: VehicleAssignmentStatus.Cancelled, label: 'Storniert', color: '#dc3545', icon: '' },
  [VehicleAssignmentStatus.Delayed]: { value: VehicleAssignmentStatus.Delayed, label: 'Verspätet', color: '#dc3545', icon: '' },
  [VehicleAssignmentStatus.Breakdown]: { value: VehicleAssignmentStatus.Breakdown, label: 'Panne', color: '#dc3545', icon: '' },
  [VehicleAssignmentStatus.Maintenance]: { value: VehicleAssignmentStatus.Maintenance, label: 'Wartung', color: '#fd7e14', icon: '' }
};

// ============================================
// ROUTE STATUS ENUMS
// ============================================

export enum RouteStatus {
  Planned = 0,
  Confirmed = 1,
  InProgress = 2,
  Completed = 3,
  Cancelled = 4,
  Delayed = 5,
  Rerouted = 6
}

export interface RouteStatusInfo {
  value: RouteStatus;
  label: string;
  color: string;
  icon: string;
}

export const ROUTE_STATUS_CONFIGS: Record<RouteStatus, RouteStatusInfo> = {
  [RouteStatus.Planned]: { value: RouteStatus.Planned, label: 'Geplant', color: '#6c757d', icon: '' },
  [RouteStatus.Confirmed]: { value: RouteStatus.Confirmed, label: 'Bestätigt', color: '#007bff', icon: '' },
  [RouteStatus.InProgress]: { value: RouteStatus.InProgress, label: 'In Bearbeitung', color: '#ffc107', icon: '' },
  [RouteStatus.Completed]: { value: RouteStatus.Completed, label: 'Abgeschlossen', color: '#28a745', icon: '' },
  [RouteStatus.Cancelled]: { value: RouteStatus.Cancelled, label: 'Abgebrochen', color: '#dc3545', icon: '' },
  [RouteStatus.Delayed]: { value: RouteStatus.Delayed, label: 'Verspätet', color: '#dc3545', icon: '' },
  [RouteStatus.Rerouted]: { value: RouteStatus.Rerouted, label: 'Umgeleitet', color: '#fd7e14', icon: '' }
};

// ============================================
// STOP STATUS ENUMS
// ============================================

export enum StopStatus {
  Planned = 0,
  Confirmed = 1,
  Approaching = 2,
  Arrived = 3,
  InProgress = 4,
  Completed = 5,
  Skipped = 6,
  Cancelled = 7,
  Delayed = 8
}

export interface StopStatusInfo {
  value: StopStatus;
  label: string;
  color: string;
  icon: string;
}

export const STOP_STATUS_CONFIGS: Record<StopStatus, StopStatusInfo> = {
  [StopStatus.Planned]: { value: StopStatus.Planned, label: 'Geplant', color: '#6c757d', icon: '' },
  [StopStatus.Confirmed]: { value: StopStatus.Confirmed, label: 'Bestätigt', color: '#007bff', icon: '' },
  [StopStatus.Approaching]: { value: StopStatus.Approaching, label: 'Anfahrt', color: '#ffc107', icon: '' },
  [StopStatus.Arrived]: { value: StopStatus.Arrived, label: 'Angekommen', color: '#28a745', icon: '' },
  [StopStatus.InProgress]: { value: StopStatus.InProgress, label: 'In Bearbeitung', color: '#17a2b8', icon: '' },
  [StopStatus.Completed]: { value: StopStatus.Completed, label: 'Abgeschlossen', color: '#28a745', icon: '' },
  [StopStatus.Skipped]: { value: StopStatus.Skipped, label: 'Übersprungen', color: '#6f42c1', icon: '' },
  [StopStatus.Cancelled]: { value: StopStatus.Cancelled, label: 'Abgebrochen', color: '#dc3545', icon: '' },
  [StopStatus.Delayed]: { value: StopStatus.Delayed, label: 'Verspätet', color: '#dc3545', icon: '' }
};

// ============================================
// PARTICIPANT STATUS ENUMS
// ============================================

export enum PaymentStatus {
  Pending = 0,
  Paid = 1,
  PartiallyPaid = 2,
  Refunded = 3,
  Waived = 4,
  Overdue = 5
}

export enum ParticipantConfirmationStatus {
  Pending = 0,
  Confirmed = 1,
  Declined = 2,
  Tentative = 3,
  Cancelled = 4,
  NoResponse = 5
}

export enum CheckInStatus {
  NotCheckedIn = 0,
  CheckedIn = 1,
  NoShow = 2,
  Late = 3,
  CheckedOut = 4
}

// ============================================
// PLANNING ENUMS (for MatchdayPlanning system)
// ============================================

export enum AnchorType {
  Departure = 0,
  Arrival = 1,
  Stopover = 2,
  Free = 3,
  Main = 4
}

export enum VehicleType {
  Bus = 0,
  Minibus = 1,
  Car = 2,
  Plane = 3,
  Train = 4,
  Taxi = 5,
  RentalCar = 6,
  Ship = 7,
  Bicycle = 8,
  OnFoot = 9
}

export enum TripType {
  Team = 0,
  Empty = 1
}

export enum PlanningStatus {
  Draft = 0,
  Confirmed = 1,
  InProgress = 2,
  Completed = 3,
  Cancelled = 4
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

export const formatMatchdayPlanStatus = (status: MatchdayPlanStatus): string => {
  return MATCHDAY_PLAN_STATUS_CONFIGS[status]?.label || 'Unbekannt';
};

export const formatTransportationAssignmentStatus = (status: TransportationAssignmentStatus): string => {
  return TRANSPORTATION_ASSIGNMENT_STATUS_CONFIGS[status]?.label || 'Unbekannt';
};

export const formatDetailPlanStatus = (status: DetailPlanStatus): string => {
  return DETAIL_PLAN_STATUS_CONFIGS[status]?.label || 'Unbekannt';
};

export const formatVehicleAssignmentStatus = (status: VehicleAssignmentStatus): string => {
  return VEHICLE_ASSIGNMENT_STATUS_CONFIGS[status]?.label || 'Unbekannt';
};

export const formatRouteStatus = (status: RouteStatus): string => {
  return ROUTE_STATUS_CONFIGS[status]?.label || 'Unbekannt';
};

export const formatStopStatus = (status: StopStatus): string => {
  return STOP_STATUS_CONFIGS[status]?.label || 'Unbekannt';
};

// Status color getters
export const getMatchdayPlanStatusColor = (status: MatchdayPlanStatus): string => {
  return MATCHDAY_PLAN_STATUS_CONFIGS[status]?.color || '#6c757d';
};

export const getTransportationAssignmentStatusColor = (status: TransportationAssignmentStatus): string => {
  return TRANSPORTATION_ASSIGNMENT_STATUS_CONFIGS[status]?.color || '#6c757d';
};

export const getDetailPlanStatusColor = (status: DetailPlanStatus): string => {
  return DETAIL_PLAN_STATUS_CONFIGS[status]?.color || '#6c757d';
};

export const getVehicleAssignmentStatusColor = (status: VehicleAssignmentStatus): string => {
  return VEHICLE_ASSIGNMENT_STATUS_CONFIGS[status]?.color || '#6c757d';
};

export const getRouteStatusColor = (status: RouteStatus): string => {
  return ROUTE_STATUS_CONFIGS[status]?.color || '#6c757d';
};

export const getStopStatusColor = (status: StopStatus): string => {
  return STOP_STATUS_CONFIGS[status]?.color || '#6c757d';
};

// Status icon getters
export const getRouteStatusIcon = (status: RouteStatus): string => {
  return ROUTE_STATUS_CONFIGS[status]?.icon || '';
};

export const getStopStatusIcon = (status: StopStatus): string => {
  return STOP_STATUS_CONFIGS[status]?.icon || '';
};
